import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { 
  User as UserIcon, 
  Mail, 
  Phone, 
  Shield, 
  Building2, 
  Save,
  ArrowLeft,
  Loader2,
  Eye,
  EyeOff,
  Key
} from 'lucide-react';
import { toast } from 'sonner';
import { userApiService, User, UserCreateRequest, UserUpdateRequest, Role, UserBranchInfo } from '../services';
import { branchApiService } from '../services/branchApi';
import { useBranch } from '../contexts/BranchContext';
import { PermissionGate } from './ui/PermissionGate';

export function UserForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  // Determine mode from URL path
  const pathname = location.pathname;
  const isEditMode = pathname.includes('/edit');
  const isViewMode = pathname.includes('/view');
  const isCreateMode = pathname.includes('/new');
  
  // If we have an ID but no specific mode, default to view mode
  const shouldDefaultToView = id && !isEditMode && !isViewMode && !isCreateMode;
  const effectiveViewMode = Boolean(isViewMode || shouldDefaultToView);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [branches, setBranches] = useState<UserBranchInfo[]>([]);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState<UserCreateRequest>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    employeeId: '',
    phoneNumber: '',
    department: '',
    designation: '',
    joinDate: new Date().toISOString().split('T')[0],
    password: '',
    isActive: true,
    defaultBranchId: undefined
  });

  const [userRoles, setUserRoles] = useState<number[]>([]);
  const [userBranches, setUserBranches] = useState<number[]>([]);
  const [originalUserData, setOriginalUserData] = useState<User | null>(null);
  const [originalUserRoles, setOriginalUserRoles] = useState<number[]>([]);
  const [originalUserBranches, setOriginalUserBranches] = useState<number[]>([]);

  // Load roles and branches
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Load real data from API
        const [rolesResponse, branchesResponse] = await Promise.all([
          userApiService.getRoles(),
          branchApiService.getBranches()
        ]);

        if (rolesResponse.success) {
          setRoles(rolesResponse.data);
        }

        // Handle branch response - it comes as Branch[] (already extracted from ApiResponse)
        if (branchesResponse && Array.isArray(branchesResponse)) {
          setBranches(branchesResponse.map(branch => ({
            id: branch.id,
            branchName: branch.branchName,
            branchCode: branch.branchCode || '',
            address: branch.address,
            isActive: branch.isActive
          })));
        }
      } catch (error) {
        // console.error('Error loading initial data:', error);
        toast.error('Failed to load roles and branches');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Load user data for edit and view modes - separate from initial data loading
  useEffect(() => {
    if ((isEditMode || effectiveViewMode) && id) {
      loadUserData();
    }
  }, [isEditMode, effectiveViewMode, id]); // Remove loading dependency

  const loadUserData = async () => {
    try {
      setLoading(true);
      const response = await userApiService.getUserById(id!);
      
      if (response.success) {
        const user = response.data;
        
        setFormData({
          username: user.username,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          employeeId: user.employeeId,
          phoneNumber: user.phoneNumber || '',
          department: user.department || '',
          designation: user.designation || '',
          joinDate: user.joinDate,
          password: '', // Don't load password
          isActive: user.isActive,
          defaultBranchId: user.defaultBranchId
        });

        // Load user roles and branches using the new dedicated APIs
        try {
          // console.log('Loading user roles and branches for user ID:', id);
          
          const [rolesResponse, branchesResponse] = await Promise.all([
            userApiService.getUserRolesByUserId(parseInt(id!), true),
            userApiService.getUserBranchesByUserId(parseInt(id!), true)
          ]);
          
          // console.log('Roles response:', rolesResponse);
          // console.log('Branches response:', branchesResponse);
          
          let finalUserRoles: number[] = [];
          let finalUserBranches: number[] = [];
          
          if (rolesResponse.success) {
            finalUserRoles = rolesResponse.data.map(ur => ur.roleId);
            setUserRoles(finalUserRoles);
            // console.log('User roles set:', finalUserRoles);
          }
          
          if (branchesResponse.success) {
            finalUserBranches = branchesResponse.data.map(ub => ub.branchId);
            setUserBranches(finalUserBranches);
            // console.log('User branches set:', finalUserBranches);
          }
          
          // Store original data for comparison during updates - AFTER loading is complete
          setOriginalUserData(user);
          setOriginalUserRoles(finalUserRoles);
          setOriginalUserBranches(finalUserBranches);
          
          console.log('✅ Original data stored:', {
            user: user.id,
            roles: finalUserRoles,
            branches: finalUserBranches
          });
          
        } catch (error) {
          // console.log('User roles/branches not available, skipping:', error);
          // Fallback: If user has a default branch, show it as accessible
          if (user.defaultBranchId) {
            const fallbackBranches = [user.defaultBranchId];
            setUserBranches(fallbackBranches);
            
            // Store original data with fallback values
            setOriginalUserData(user);
            setOriginalUserRoles([]);
            setOriginalUserBranches(fallbackBranches);
          } else {
            // Store original data with empty arrays if no fallback
            setOriginalUserData(user);
            setOriginalUserRoles([]);
            setOriginalUserBranches([]);
          }
        }
      } else {
        toast.error(response.message || 'Failed to load user');
        navigate('/users');
      }
    } catch (error) {
      // console.error('Error loading user:', error);
      toast.error('Failed to load user');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  // Smart partial update function that only sends changed data
  const buildPartialUpdateData = async (updateData: UserUpdateRequest) => {
    if (!originalUserData) {
      // If no original data, send everything (fallback)
      // console.log('⚠️ No original data available, sending complete payload');
      return {
        ...updateData,
        roles: userRoles.map(roleId => ({ roleId, isActive: true })),
        branches: userBranches.map(branchId => ({ branchId, isActive: true }))
        // No preferences - those are handled separately in user profile
      };
    }

    // Build partial update data - only include changed fields
    const partialData: any = {};
    let hasChanges = false;

    // Check which user fields have changed
    Object.keys(updateData).forEach(key => {
      const field = key as keyof UserUpdateRequest;
      const currentValue = updateData[field];
      const originalValue = originalUserData[field];
      
      if (currentValue !== originalValue) {
        partialData[field] = currentValue;
        hasChanges = true;
        // console.log(`🔄 Field '${field}' changed:`, { from: originalValue, to: currentValue });
      }
    });
    // Check if roles have changed
    const rolesChanged = JSON.stringify(userRoles.sort()) !== JSON.stringify(originalUserRoles.sort());
    if (rolesChanged) {
      partialData.roles = userRoles.map(roleId => ({ roleId, isActive: true }));
      hasChanges = true;
      console.log(`🔄 Roles changed:`, { 
        from: originalUserRoles.sort(), 
        to: userRoles.sort() 
      });
    } else {
      // console.log(`✅ Roles unchanged:`, userRoles.sort());
    }

    // Check if branches have changed
    const branchesChanged = JSON.stringify(userBranches.sort()) !== JSON.stringify(originalUserBranches.sort());
    if (branchesChanged) {
      partialData.branches = userBranches.map(branchId => ({ branchId, isActive: true }));
      hasChanges = true;
      console.log(`🔄 Branches changed:`, { 
        from: originalUserBranches.sort(), 
        to: userBranches.sort() 
      });
    } else {
      // console.log(`✅ Branches unchanged:`, userBranches.sort());
    }

    // No preferences - those are handled separately in user profile

    if (!hasChanges) {
      // console.log(`⚠️ No changes detected - payload will be minimal`);
    }

    // console.log('📤 Final partial update payload:', partialData);
    console.log('📊 Change summary:', {
      userFieldsChanged: Object.keys(partialData).filter(key => !['roles', 'branches'].includes(key)).length,
      rolesChanged,
      branchesChanged,
      totalPayloadSize: JSON.stringify(partialData).length
    });

    return partialData;
  };

  const handleInputChange = (field: keyof UserCreateRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleToggle = (roleId: number) => {
    setUserRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleBranchToggle = (branchId: number) => {
    setUserBranches(prev => 
      prev.includes(branchId) 
        ? prev.filter(id => id !== branchId)
        : [...prev, branchId]
    );
  };

  const validateForm = (): boolean => {
    if (!formData.username.trim()) {
      toast.error('Username is required');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return false;
    }
    if (!formData.firstName.trim()) {
      toast.error('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      toast.error('Last name is required');
      return false;
    }
    if (!isEditMode && !formData.password.trim()) {
      toast.error('Password is required for new users');
      return false;
    }
    if (formData.password && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      if (isEditMode) {
        const updateData: UserUpdateRequest = {
          // Include username to maintain its existing value (should not be changed by user)
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          employeeId: formData.employeeId,
          phoneNumber: formData.phoneNumber,
          department: formData.department,
          designation: formData.designation,
          joinDate: formData.joinDate,
          isActive: formData.isActive,
          defaultBranchId: formData.defaultBranchId
        };


        
        // Smart partial update: Only send changed data to avoid overwriting unchanged fields
        // This approach:
        // 1. Reduces API payload size
        // 2. Prevents accidental overwrites of unchanged data
        // 3. Uses the single /auth/users/{id}/with-roles-and-branches endpoint for User Management
        // 4. Handles roles and branches changes intelligently
        // 5. No preferences - those are handled separately in user profile
        // 6. Includes operatedBy for audit trail
        // 7. ONLY sends branches/roles if they actually changed
        const userData = await buildPartialUpdateData(updateData);
        
        // TODO: Get the actual current user ID from authentication context
        const operatedBy = 1; // Replace with actual current user ID
        
        await userApiService.updateUserWithRolesAndBranches(parseInt(id!), userData, operatedBy);

        toast.success('User updated successfully!');
      } else {
        // For now, use the selected branch as branchId and a default createdBy value
        // TODO: Get the actual current user ID from authentication context
        const branchId = formData.defaultBranchId || 1; // Default to first branch if none selected
        const createdBy = 1; // TODO: Replace with actual current user ID
        
        const createResponse = await userApiService.createUser(formData, branchId, createdBy);
        
        if (createResponse.success) {
          // For new users, assign roles and branches after creation
          const newUserId = createResponse.data.id;
          const assignedBy = 1; // TODO: Replace with actual current user ID
          
          // Assign multiple roles to new user
          if (userRoles.length > 0) {
            await userApiService.assignMultipleRolesToUser(newUserId, userRoles, assignedBy);
          }
          
          // Assign multiple branches to new user
          if (userBranches.length > 0) {
            await userApiService.assignMultipleBranchesToUser(newUserId, userBranches, assignedBy);
          }
          
          toast.success('User created successfully!');
        }
      }

      navigate('/users');
    } catch (error) {
      // console.error('Error saving user:', error);
      toast.error('Failed to save user');
    } finally {
      setSaving(false);
    }
  };



  const handleReset = () => {
    if (isEditMode) {
      loadUserData();
    } else {
      setFormData({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        employeeId: '',
        phoneNumber: '',
        department: '',
        designation: '',
        joinDate: new Date().toISOString().split('T')[0],
        password: '',
        isActive: true,
        defaultBranchId: undefined
      });
      setUserRoles([]);
      setUserBranches([]);
      setOriginalUserData(null);
      setOriginalUserRoles([]);
      setOriginalUserBranches([]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  // User management route (from backend permissions)
  const USER_ROUTE = '/users';

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/users')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Users
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {effectiveViewMode ? 'View User' : isEditMode ? 'Edit User' : 'Create User'}
            </h1>
            <p className="text-gray-600 mt-1">
              {effectiveViewMode ? 'View user details' : isEditMode ? 'Update user information' : 'Add a new user to the system'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <UserIcon className="w-5 h-5" />
                <span>Basic Information</span>
              </CardTitle>
              <CardDescription>
                Enter the user's basic information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder="Enter username"
                    disabled={effectiveViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    disabled={effectiveViewMode}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                    disabled={effectiveViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                    disabled={effectiveViewMode}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Enter phone number"
                  disabled={effectiveViewMode}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employeeId">Employee ID *</Label>
                  <Input
                    id="employeeId"
                    value={formData.employeeId}
                    onChange={(e) => handleInputChange('employeeId', e.target.value)}
                    placeholder="Enter employee ID"
                    disabled={effectiveViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="joinDate">Join Date *</Label>
                  <Input
                    id="joinDate"
                    type="date"
                    value={formData.joinDate}
                    onChange={(e) => handleInputChange('joinDate', e.target.value)}
                    disabled={effectiveViewMode}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    placeholder="Enter department"
                    disabled={effectiveViewMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="designation">Designation</Label>
                  <Input
                    id="designation"
                    value={formData.designation}
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                    placeholder="Enter designation"
                    disabled={effectiveViewMode}
                  />
                </div>
              </div>

              {!isEditMode && (
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter password"
                      disabled={effectiveViewMode}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={effectiveViewMode}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => handleInputChange('isActive', checked)}
                  disabled={effectiveViewMode}
                />
                <Label htmlFor="isActive">Active User</Label>
              </div>
            </CardContent>
          </Card>

          {/* Roles and Permissions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span>Roles and Permissions</span>
              </CardTitle>
              <CardDescription>
                Assign roles to the user
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {roles.map((role) => (
                  <div key={role.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`role-${role.id}`}
                      checked={userRoles.includes(role.id)}
                      onChange={() => handleRoleToggle(role.id)}
                      disabled={effectiveViewMode}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <Label htmlFor={`role-${role.id}`} className="text-sm font-medium">
                      {role.name}
                    </Label>
                    {role.description && (
                      <span className="text-xs text-gray-500">({role.description})</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Default Branch */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="w-5 h-5" />
                <span>Default Branch</span>
              </CardTitle>
              <CardDescription>
                Select the user's default branch
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="defaultBranch">Default Branch</Label>
                <Select
                  value={formData.defaultBranchId?.toString() || ''}
                  onValueChange={(value) => handleInputChange('defaultBranchId', value ? parseInt(value) : undefined)}
                  disabled={effectiveViewMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select default branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id.toString()}>
                        {branch.branchName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Branch Access */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="w-5 h-5" />
                <span>Branch Access</span>
              </CardTitle>
              <CardDescription>
                Select branches the user can access
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {branches.map((branch) => (
                  <div key={branch.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`branch-${branch.id}`}
                      checked={userBranches.includes(branch.id)}
                      onChange={() => handleBranchToggle(branch.id)}
                      disabled={effectiveViewMode}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <Label htmlFor={`branch-${branch.id}`} className="text-sm font-medium">
                      {branch.branchName}
                    </Label>
                    <span className="text-xs text-gray-500">({branch.branchCode})</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
                        {!effectiveViewMode && (
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <PermissionGate route={USER_ROUTE} action={isEditMode ? "edit" : "create"}>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full bg-primary-600 hover:bg-primary-700"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {isEditMode ? 'Update User' : 'Create User'}
                      </>
                    )}
                  </Button>
                </PermissionGate>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={saving}
                  className="w-full"
                >
                  Reset
                </Button>
              </CardContent>
            </Card>
          )}

          {/* User Summary */}
          <Card>
            <CardHeader>
              <CardTitle>User Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Username:</span>
                <span className="text-sm font-medium">{formData.username || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Full Name:</span>
                <span className="text-sm font-medium">
                  {formData.firstName && formData.lastName 
                    ? `${formData.firstName} ${formData.lastName}` 
                    : 'Not set'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Email:</span>
                <span className="text-sm font-medium">{formData.email || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Employee ID:</span>
                <span className="text-sm font-medium">{formData.employeeId || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Department:</span>
                <span className="text-sm font-medium">{formData.department || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Join Date:</span>
                <span className="text-sm font-medium">{formData.joinDate || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <span className={`text-sm font-medium ${formData.isActive ? 'text-green-600' : 'text-red-600'}`}>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Roles Assigned:</span>
                <span className="text-sm font-medium">{userRoles.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Branches Access:</span>
                <span className="text-sm font-medium">{userBranches.length}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
